import type { PublicPlan } from './signup-api.js';

const STORAGE_KEY = 'gf-signup-pending';

export interface PendingSignup {
  signupId: string;
  clientSecret: string;
  intentType: 'setup' | 'payment';
  plan: PublicPlan;
  /**
   * Set once payment was handed off and the server is provisioning. Survives a
   * browser reload (sessionStorage) after the Stripe return params are stripped,
   * so a paid customer is never dropped back on the plan picker.
   */
  phase?: 'status';
}

/** Record that payment finished and the signup is now provisioning. */
export function markPendingSignupProvisioning(): void {
  const pending = loadPendingSignup();
  if (pending) savePendingSignup({ ...pending, phase: 'status' });
}

/** Persist the in-flight signup so a Stripe redirect return can resume it. */
export function savePendingSignup(pending: PendingSignup): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable (private mode): the flow still works without redirects.
  }
}

export function loadPendingSignup(): PendingSignup | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingSignup>;
    if (
      typeof parsed.signupId !== 'string' ||
      typeof parsed.clientSecret !== 'string' ||
      (parsed.intentType !== 'setup' && parsed.intentType !== 'payment') ||
      typeof parsed.plan !== 'object' ||
      parsed.plan === null
    ) {
      return null;
    }
    return {
      ...(parsed as PendingSignup),
      phase: parsed.phase === 'status' ? 'status' : undefined,
    };
  } catch {
    return null;
  }
}

export type RedirectOutcome = 'succeeded' | 'processing' | 'failed';

/** Reads Stripe's `redirect_status` from the return URL, or null if absent. */
export function readRedirectStatus(search: string): RedirectOutcome | null {
  const status = new URLSearchParams(search).get('redirect_status');
  if (status === 'succeeded') return 'succeeded';
  if (status === 'pending' || status === 'processing' || status === 'requires_action') {
    return 'processing';
  }
  if (status === 'failed' || status === 'canceled' || status === 'requires_payment_method') {
    return 'failed';
  }
  return null;
}

/** Strips Stripe's return params so a refresh does not re-trigger resume. */
export function stripRedirectParams(): void {
  const url = new URL(window.location.href);
  for (const key of [
    'redirect_status',
    'payment_intent',
    'payment_intent_client_secret',
    'setup_intent',
    'setup_intent_client_secret',
  ]) {
    url.searchParams.delete(key);
  }
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
}
